<template>
  <div>
    <v-card class="pa-2 d-print-none">
      <v-row no-gutters class="align-center">
        <v-col
          cols="12"
          md="9"
          class="d-flex"
          style="gap: 10px;"
          :class="$vuetify.breakpoint.mobile ? 'flex-column' : ''"
        >
          <div class="d-flex justify-space-between">
            <MakePayment
              v-if="$permission('107')"
              :selectedAffiliates="selectedAffiliates"
              @removeAffiliates="removeAffiliates"
            />

            <div v-if="$vuetify.breakpoint.xs">
              <v-btn-toggle v-model="toggle" dense>
                <v-btn @click="breakpoint = 0" small>
                  <v-icon>mdi-view-list</v-icon>
                </v-btn>

                <v-btn @click="breakpoint = 'xs'" small>
                  <v-icon>mdi-view-grid</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>

          <div class="align-self-start">
            <v-btn
              small
              class="white black--text d-print-none"
              @click="$printReport()"
            >
              <v-icon small left>mdi-printer</v-icon>
              {{ $t("print") }}
            </v-btn>
          </div>
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            :class="$vuetify.breakpoint.smAndDown ? 'mt-3' : ''"
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('search')"
            single-line
            hide-details
            dense
            solo-inverted
            clearable
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <v-data-table
      v-model="selectedAffiliates"
      :show-select="$permission('107') ? true : false"
      item-key="affiliate_id"
      :headers="headers"
      :items="items"
      :search="search"
      :items-per-page="10"
      class="elevation-1 mb-3"
      :loading="loading"
      :loading-text="loadingText"
      :no-data-text="noAffiliates"
      @toggle-select-all="selectAll"
      :mobile-breakpoint="breakpoint"
      :footer-props="{
        'items-per-page-text': this.$t('rowsPerPage'),
      }"
    >
      <template v-slot:top>
        <div class="pa-2" v-show="showSelectAllAsButton">
          <v-btn
            block
            @click="selectAll"
            color="primary"
            small
            :disabled="possibleSelectedAffiliates.length === 0"
          >
            {{
              possibleSelectedAffiliates.length === 0 ||
              possibleSelectedAffiliates.length > selectedAffiliates.length
                ? $t("payments.selectElegibleAffiliates")
                : $t("payments.removeElegibleAffiliates")
            }}
          </v-btn>
        </div>
      </template>

      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          :value="isSelected"
          :readonly="item.disableCheckbox"
          :disabled="item.disableCheckbox"
          @input="select($event)"
          :ripple="false"
        ></v-simple-checkbox>
      </template>

      <template v-for="h in headers" v-slot:[`header.${h.value}`]="{}">
        <div :key="h.value">
          <span>{{ h.text }}</span>
          <v-tooltip top v-if="h.value === 'bound_player'">
            <template v-slot:activator="{ on }">
              <v-icon color="primary" v-on="on" small class="pl-2">
                mdi-information-outline
              </v-icon>
            </template>
            <span v-html="$t('payments.eligibleForPayment')" />
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.affiliate_id="{ item }">
        <v-btn x-small @click="$copy(item.affiliate_id)">
          {{ item.affiliate_id }}
        </v-btn>
      </template>

      <template v-slot:item.affiliate_name="{ item }">
        <a
          class="blue--text"
          @click="
            $profile.showProfileDrawer(
              item.affiliate_id,
              `/${$prefix()}/affiliates/getAffiliate`
            )
          "
        >
          <v-icon color="blue" small>mdi-account</v-icon>
          {{ item.affiliate_name }}
        </a>
      </template>

      <template v-slot:item.bound_player="{ item }">
        <div
          v-if="item.bound_player"
          class="d-flex align-center"
          style="gap: 5px;"
        >
          <v-icon small color="green">
            mdi-check
          </v-icon>
          <a style="cursor: pointer;" @click="toPlayer(item)">
            {{ item.player_login }}
          </a>
        </div>
        <div v-else>
          <v-icon small color="red">
            mdi-close
          </v-icon>
          {{ $t("no") }}
        </div>
      </template>

      <template v-slot:item.gross_value="{ item }">
        <span :class="getColor(item.gross_value)">
          {{ getPortugueseMoney(item.gross_value) }}
        </span>
      </template>

      <template v-slot:item.prize_value="{ item }">
        <span
          :class="Number(item.prize_value) == 0 ? 'grey--text' : 'red--text'"
        >
          {{ getPortugueseMoney(item.prize_value) }}
        </span>
      </template>

      <template v-slot:item.ggr_value="{ item }">
        <span :class="getColor(item.ggr_value)">
          {{ getPortugueseMoney(item.ggr_value) }}
        </span>
      </template>

      <template v-slot:item.ngr_value="{ item }">
        <span :class="getColor(item.ngr_value)">
          {{ getPortugueseMoney(item.ngr_value) }}
        </span>
      </template>

      <template v-slot:item.fee_value="{ item }">
        <span :class="Number(item.fee_value) == 0 ? 'grey--text' : 'red--text'">
          {{ getPortugueseMoney(item.fee_value) }}
        </span>
      </template>

      <template v-slot:item.rev_share_value="{ item }">
        <span
          :class="
            Number(item.rev_share_value) == 0 ? 'grey--text' : 'red--text'
          "
        >
          {{ getPortugueseMoney(item.rev_share_value) }}
        </span>
      </template>

      <template v-slot:item.cpa_commission="{ item }">
        <span
          :class="Number(item.cpa_commission) == 0 ? 'grey--text' : 'red--text'"
        >
          {{ getPortugueseMoney(item.cpa_commission) }}
        </span>
      </template>

      <template v-slot:item.commission_value="{ item }">
        <span
          :class="
            Number(item.commission_value) == 0 ? 'grey--text' : 'red--text'
          "
        >
          {{ getPortugueseMoney(item.commission_value) }}
        </span>
      </template>
    </v-data-table>

    <div v-if="Object.keys(resume).length > 0">
      <Resume :resume="resume" :resumePrint="resumePrint" />
    </div>
  </div>
</template>

<script>
import MakePayment from "./MakePayment";
import Resume from "../resume";

export default {
  data: () => ({
    items: [],
    search: "",
    loading: false,
    selectedAffiliates: [],
    resume: {},
    toggle: 0,
    breakpoint: 0,
    resumePrint: "",
  }),

  created() {
    if (this.$vuetify.breakpoint.smAndDown) {
      this.breakpoint = "xs";
      this.toggle = 1;
    }
  },

  components: { MakePayment, Resume },

  watch: {
    saveFilters(val) {
      val && this.fetchPendingPayments();
    },
  },

  computed: {
    headers() {
      const headers = [
        { text: this.$t("idAffiliate"), value: "affiliate_id" },
        // { text: this.$t("mRegional"), value: "regional_name" },
        // { text: this.$t("mLocal"), value: "local_name" },
        { text: this.$t("affiliateName"), value: "affiliate_name" },
        { text: this.$t("payments.linkedPlayer"), value: "bound_player" },
        {
          text: this.$t("revenues"),
          value: "gross_value",
          align: "start",
        },
        {
          text: this.$t("awards"),
          value: "prize_value",
          align: "start",
        },
        {
          text: "GGR",
          value: "ggr_value",
          align: "start",
        },
        {
          text: this.$t("feeValue"),
          value: "fee_value",
          align: "start",
        },
        {
          text: "NGR",
          value: "ngr_value",
          align: "start",
        },
      ];

      const showCpa = false;
      if (showCpa) {
        headers.push(
          {
            text: this.$t("revShareValue"),
            value: "rev_share_value",
           align: "start",
          },
          {
            text: this.$t("valueCpa"),
            value: "cpa_commission",
            align: "start",
          }
        );
      }

      headers.push({
        text: this.$t("commissionValue"),
        value: "commission_value",
        align: "start",
      });

      return headers;
    },
    possibleSelectedAffiliates() {
      return this.items.filter((item) => !item.disableCheckbox);
    },
    regionalId() {
      return this.$store.state.filters.autocompleteRegionals.id;
    },
    localId() {
      return this.$store.state.filters.autocompleteLocals.id;
    },
    saveFilters() {
      return this.$store.state.filters.save;
    },
    loadingText() {
      return this.$t("payments.loadingAffiliates");
    },
    noAffiliates() {
      return this.$t("payments.noDataFound");
    },
    showSelectAllAsButton() {
      return this.items.length > 0 &&
        this.$vuetify.breakpoint.xs &&
        this.breakpoint === "xs" &&
        this.$permission("107")
        ? true
        : false;
    },
  },

  methods: {
    getColor(value) {
      return value == 0
        ? "grey--text"
        : value < 0
        ? "red--text"
        : "green--text";
    },
    fetchPendingPayments() {
      this.reset();

      const { year, month } = this.$store.getters.getMonthAndYearOfPayment;

      const body = {
        regional_id: this.regionalId,
        local_id: this.localId,
        year,
        month,
      };

      this.loading = true;

      this.$axios
        .post(
          `/${this.$prefix()}/financial/listAffiliatesToPay`,
          this.$formdata(body)
        )
        .then((res) => {
          this.items = res.data.dados.filter(
            (item) =>
              parseFloat(item.total_active_players) >=
                parseFloat(item.min_active_users_to_commission) &&
              Number(item.value) > 0
          );

          this.items = this.items.map((item) => {
            const ggr_value =
              Number(item.gross_value) - Number(item.prize_value);
            const fee_percentage = Number(item.fee_percentage) / 100;
            const fee_value =
              ggr_value > 0 ? Number(ggr_value) * fee_percentage : 0;
            const ngr_value = Number(item.ngr);

            item.ggr_value = ggr_value;
            item.fee_value = fee_value;
            item.ngr_value = ngr_value;
            item.commission_value = item.value;

            return {
              ...item,
              gross_value: Number(item.gross_value).toFixed(2),
              prize_value: Number(item.prize_value).toFixed(2),
              ggr_value: Number(item.ggr_value).toFixed(2),
              fee_value: Number(item.fee_value).toFixed(2),
              ngr_value: Number(item.ngr_value).toFixed(2),
              rev_share_value: (
                Number(item.commission_value) - Number(item.cpa_commission)
              ).toFixed(2),
              commission_value: Number(item.commission_value).toFixed(2),
              disableCheckbox:
                Number(item.commission_value) === 0 || !item.bound_player,
            };
          });

          if (this.items.length > 0) {
            this.items.sort(
              (a, b) =>
                parseFloat(b.commission_value) - parseFloat(a.commission_value)
            );

            this.buildResume();
          }
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildResume() {
      const resume = {};

      resume.affiliates_count = { classColor: "", value: this.items.length };

      resume.not_eligible = {
        classColor: "",
        value: this.items.length - this.possibleSelectedAffiliates.length,
      };

      resume.eligible = {
        classColor: "",
        value: this.possibleSelectedAffiliates.length,
      };

      const totalPaymentValueEligible = this.possibleSelectedAffiliates.reduce(
        (n, { commission_value }) => n + Number(commission_value),
        0
      );

      resume.gross_value = this.getResumeItem({
        key: "gross_value",
      });

      resume.prize_value = this.getResumeItem({
        key: "prize_value",
      });

      resume.ggr_value = this.getResumeItem({
        key: "ggr_value",
      });

      resume.fee_value = this.getResumeItem({
        key: "fee_value",
      });

      resume.ngr_value = this.getResumeItem({
        key: "ngr_value",
      });

      resume.value = {
        value: this.getPortugueseMoney(totalPaymentValueEligible),
      };

      resume.valueAll = this.getResumeItem({
        key: "commission_value",
      });

      const resumePrint = `
        <table>
          <tr>${resume.affiliates_count.value.toString().replace(".", ",")}</tr>
          <tr>${resume.not_eligible.value.toString().replace(".", ",")}</tr>
          <tr>${resume.eligible.value.toString().replace(".", ",")}</tr>
          <tr>${resume.gross_value.value.toString().replace(".", ",")}</tr>
          <tr>${resume.prize_value.value.toString().replace(".", ",")}</tr>
          <tr>${resume.ggr_value.value.toString().replace(".", ",")}</tr>
          <tr>${resume.fee_value.value.toString().replace(".", ",")}</tr>
          <tr>${resume.ngr_value.value.toString().replace(".", ",")}</tr>
          <tr>${resume.value.value.toString().replace(".", ",")}</tr>
          <tr>${resume.valueAll.value.toString().replace(".", ",")}</tr>
        </table>
        `;

      this.resumePrint = resumePrint;

      this.resume = resume;
    },
    selectAll() {
      if (
        this.possibleSelectedAffiliates.length > this.selectedAffiliates.length
      ) {
        this.selectedAffiliates = this.possibleSelectedAffiliates;
      } else {
        this.selectedAffiliates = [];
      }
    },
    getResumeItem({ key }) {
      const value = this.items
        .reduce((n, item) => n + Number(item[key]), 0)
        .toFixed(2);

      return {
        value: this.getPortugueseMoney(value),
        valueWithoutFormat: value,
      };
    },
    toPlayer(item) {
      const prefix =
        this.$prefix() === "colab" ? "Collaborator" : "Administrator";

      const routeName = `${prefix}AccountabilityOnlinePlayersOnlineSearch`;

      this.$router.push({
        name: routeName,
        query: { id_user: item.bound_player },
      });
    },
    reset() {
      this.search = "";
      this.items = [];
      this.selectedAffiliates = [];
      this.resume = {};
    },
    removeAffiliates(removed) {
      this.search = "";
      this.selectedAffiliates = [];
      this.items = this.items.filter(
        (item) => !removed.includes(item.affiliate_id)
      );
      this.buildResume();
    },
    getPortugueseMoney(value) {
      return Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
      });
    },
  },
};
</script>
